import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import CommonImage from '@/components/CommonImage';
import AddTaskModal from '@/components/AddTaskModal';
import SampleComment from '@/components/SampleComment';
import { toastError, toastSuccess } from '@/components/Toast';
import ConfirmationModal from '@/features/crm/components/ConfirmationModal';
import ApiClient from '@/lib/apiClient';
import { onApproveRequest } from '@/services/costingService';
import { clearCurrentActivePage, setCurrentActivePage } from '@/store/actions/ActivePageAction';
import { clearDesignViewModalInfo, setDesignViewModalInfo } from '@/store/actions/DesignViewModalAction';
import { checkUserType, getShortName, replaceSpace } from '@/utils/Util';
import HorizontalScroll from '@/components/HorizontalScroll';
import {
	COSTING_OFFER_STATUS,
	dashboardGroupBy,
	formatDashboardDate,
	renderLeadTimeVariance
} from '@/features/my-dashborad/utils';

import { useQuantityWiseUpdatePriceMutation } from '../../api/myDashboardApi';

import CostingEodModal from './CostingEodModal';

const ConstingListView = ({
	listViewResponse,
	selectedIds = [],
	setSelectedIds = () => {},
	onRefreshList = () => {},
	selectedGroupBy = null
}) => {
	const dispatch = useDispatch();
	const [quantityWiseUpdatePrice, { isLoading: isUpdatingPrice }] = useQuantityWiseUpdatePriceMutation();

	const [cellId, setCellId] = useState('');
	const [targetValues, setTargetValues] = useState({});
	const [offerValues, setOfferValues] = useState({});
	const [remarkValues, setRemarkValues] = useState({});
	const [showEodModal, setShowEodModal] = useState(false);
	const [isShowTaskUpdateModal, setIsShowTaskUpdateModal] = useState(false);
	const [confirmationModal, setConfirmationModal] = useState({ isOpen: false, type: null });
	const [selectAll, setSelectAll] = useState(false);
	const [selectedProductIds, setSelectedProductIds] = useState([]);
	const [selectedTaskProductIds, setSelectedTaskProductIds] = useState([]);
	const [selectedItem, setSelectedItem] = useState([]);
	const [selectedProductId, setSelectedProductId] = useState(null);
	const [selectedIndex, setSelectedIndex] = useState(null);
	const [selectedInitialCostingId, setSelectedInitialCostingId] = useState(null);
	const [isShowCosting, setIsShowCosting] = useState(false);
	const [showComment, setShowComment] = useState(false);
	const [conversationProductId, setConversationProductId] = useState(null);
	const prevTarget = useRef('');
	const prevOffer = useRef('');
	const prevRemark = useRef('');
	const elementRef = useRef(null);
	const productionPriceMissing = 'Production price is missing to move forward';
	const notSimilarStatus = 'You have to select styles from similar status';
	const TARGET_FIELD = 'TARGET_PRICE';
	const OFFER_FIELD = 'OFFER_PRICE';
	const REMARK_FIELD = 'REMARKS';

	//group wise
	const rows = Array.isArray(listViewResponse) ? listViewResponse : [];
	const groupedRows = useMemo(() => {
		if (!selectedGroupBy?.id || !rows.length) return null;
		if (selectedGroupBy.id === 'REQUEST') {
			return rows.reduce((acc, item) => {
				const requestId = item?.costingJourneyDetailsResponse?.requestId;
				const key = requestId != null && requestId !== '' ? requestId : 'No request';
				acc[key] = acc[key] || [];
				acc[key].push(item);
				return acc;
			}, {});
		}
		return dashboardGroupBy(rows, selectedGroupBy.id);
	}, [rows, selectedGroupBy?.id]);

	const displayGroupEntries = useMemo(() => {
		if (!groupedRows) return null;
		if (selectedGroupBy?.id === 'REQUEST') {
			return Object.entries(groupedRows).map(([groupKey, items]) => {
				if (groupKey === '__NO_REQUEST__') return ['No request', items];
				const firstItem = items?.[0];
				const requestInfo = firstItem?.costingJourneyDetailsResponse;
				const brandName = requestInfo?.brandResponse?.name;
				const rawRef = requestInfo?.requestReferenceNumber;
				const systemReferenceNumber = rawRef != null && rawRef !== '' ? `#${rawRef}` : '';
				const collectionName = firstItem?.collectionName;
				const requestTitle = [brandName, systemReferenceNumber, collectionName]
					.map((value) => (value != null ? value : ''))
					.join(' | ');
				return [requestTitle || `Request ${groupKey}`, items];
			});
		}
		return Object.entries(groupedRows);
	}, [groupedRows, selectedGroupBy?.id]);

	//target wise
	const getInitialTargetValue = (item) =>
		item?.target ?? item?.costingJourneyDetailsResponse?.quoteItemResponse?.target ?? '';
	const getDisplayTargetValue = (item) => {
		const rowTargetId =
			item?.costingJourneyDetailsResponse?.quoteItemResponse?.quantityWiseCostingId ?? item?.id;

		if (Object.prototype.hasOwnProperty.call(targetValues, rowTargetId)) {
			return targetValues[rowTargetId];
		}
		return getInitialTargetValue(item);
	};

	const getRowTargetId = (item) =>
		item?.costingJourneyDetailsResponse?.quoteItemResponse?.quantityWiseCostingId ?? item?.id;

	const getDisplayRemarkValue = (item) => {
		const rowTargetId = getRowTargetId(item);
		if (Object.prototype.hasOwnProperty.call(remarkValues, rowTargetId)) {
			return remarkValues[rowTargetId];
		}
		return item?.costingJourneyDetailsResponse?.quoteItemResponse?.remarks ?? '';
	};

	const onSetCellId = (id, field) => setCellId(`${id}_${field}`);
	const getTargetCellId = (id) => `${id}_${TARGET_FIELD}`;

	const resetAll = () => {
		setCellId('');
		setSelectedItem([]);
	};

	const handleCommentClose = () => {
		dispatch(clearCurrentActivePage());
		setConversationProductId(null);
		setShowComment(false);
	};

	useEffect(() => {
		if (!selectedIds?.length) {
			setSelectedProductIds([]);
			setSelectedTaskProductIds([]);
			setSelectAll(false);
			setSelectedItem([]);
		}
	}, [selectedIds]);

	useEffect(() => {
		if (selectedProductId !== null) {
			const findSetItem = listViewResponse?.find(
				(item) => selectedProductId === (item?.productId ?? item?.id)
			);
			dispatch(
				setDesignViewModalInfo({
					mergeProductList:
						findSetItem &&
						findSetItem?.setMemberList &&
						findSetItem?.setMemberList.map((member) => member.productId),
					isShowMergeTechPack: findSetItem?.isSet,
					id: selectedProductId,
					onCloseViewModal: () => {
						setSelectedInitialCostingId(null);
						setSelectedProductId(null);
						setSelectedIndex(null);
						setIsShowCosting(false);
						dispatch(clearDesignViewModalInfo({}));
					},
					designIdList: [
						...new Set(
							listViewResponse?.map((data) => data?.productId ?? data?.id).filter(Boolean)
						)
					],
					source: 'PRODUCT_LIST',
					isCostingRequestFromBuyer: true,
					initialCostingId: selectedInitialCostingId,
					onUpdateCostingStatus: () => onRefreshList(),
					costingItemList: listViewResponse,
					selectedIndex: selectedIndex,
					isShowCostingPanel: isShowCosting,
					isShowCostingButton: true
				})
			);
		}
	}, [
		dispatch,
		isShowCosting,
		listViewResponse,
		onRefreshList,
		selectedIndex,
		selectedInitialCostingId,
		selectedProductId
	]);

	const onChangeCheckbox = (e, item) => {
		const quantityWiseCostingId = getRowTargetId(item);
		const productId = item?.productId ?? item?.id;
		let temp = [...selectedIds];
		let tempProductIds = [...selectedProductIds];

		if (e.target.checked) {
			temp = [...temp, quantityWiseCostingId.toString()];
			tempProductIds = [...tempProductIds, productId.toString()];
		} else {
			temp = temp.filter((id) => id !== quantityWiseCostingId.toString());
			tempProductIds = tempProductIds.filter((id) => id !== productId.toString());
		}

		setSelectedIds(temp);
		setSelectedProductIds(tempProductIds);
		setSelectedTaskProductIds(tempProductIds);
		const updatedSelectedItems =
			listViewResponse?.filter((row) => temp.includes(getRowTargetId(row)?.toString())) ?? [];
		setSelectedItem(updatedSelectedItems);
	};

	const handleSelectAllCostingItem = (e) => {
		if (e.target.checked) {
			const allIds = listViewResponse?.map((row) => getRowTargetId(row).toString()) ?? [];
			const allProductIds =
				listViewResponse?.map((row) => (row?.productId ?? row?.id).toString()) ?? [];
			const allItems = listViewResponse?.map((row) => row);
			setSelectedIds(allIds);
			setSelectedProductIds(allProductIds);
			setSelectedTaskProductIds(allProductIds);
			setSelectedItem(allItems);
		} else {
			setSelectedIds([]);
			setSelectedProductIds([]);
			setSelectedTaskProductIds([]);
			setSelectedItem([]);
		}
	};

	const getStatusValue = (item) =>
		item?.currentStatus?.status ??
		item?.costingJourneyDetailsResponse?.quoteItemResponse?.status ??
		item?.costingJourneyDetailsResponse?.lastStatusName ??
		'';

	const checkIfAllProductionPriceAvailable = () => {
		const selectedItems = listViewResponse?.filter((item) =>
			selectedIds.includes(getRowTargetId(item)?.toString())
		);
		return selectedItems?.every((item) => {
			const productionPrice =
				item?.productionPrice ??
				item?.costingJourneyDetailsResponse?.quoteItemResponse?.productionPrice;
			return !!productionPrice;
		});
	};

	const checkIfSameSimilarStatus = () => {
		const selectedItems = listViewResponse?.filter((item) =>
			selectedIds.includes(getRowTargetId(item)?.toString())
		);
		const firstVal = getStatusValue(selectedItems?.[0]);
		const differentVal = selectedItems?.find((item) => getStatusValue(item) !== firstVal);
		return differentVal ? 'DIFFERENT' : firstVal;
	};

	const checkIfDisable = (name) =>
		checkIfSameSimilarStatus() === replaceSpace(name)?.toUpperCase() ? false : true;

	const markAll = (mark) => {
		if (mark) {
			const allIds = listViewResponse?.map((item) => getRowTargetId(item)?.toString()) ?? [];
			const allProductIds =
				listViewResponse?.map((item) => (item?.productId ?? item?.id)?.toString()) ?? [];
			const allItems = listViewResponse?.map((item) => item) ?? [];
			setSelectedIds(allIds);
			setSelectedProductIds(allProductIds);
			setSelectedTaskProductIds(allProductIds);
			setSelectAll(true);
			setSelectedItem(allItems);
		} else {
			setSelectedIds([]);
			setSelectedProductIds([]);
			setSelectedTaskProductIds([]);
			setSelectAll(false);
			setSelectedItem([]);
		}
	};

	const onSendOffer = async () => {
		try {
			const { data } = await ApiClient.POST('sendOffer', {}, `?ids=${selectedIds}`);
			if (data?.success) {
				await onRefreshList();
				toastSuccess(data?.message || 'Offer sent successfully');
				markAll(false);
			}
		} catch (error) {
			toastError(error?.response?.data?.message || 'Something went wrong');
		}
	};

	const onApproveOrRejct = async () => {
		try {
			await onApproveRequest(selectedIds);
			await onRefreshList();
			toastSuccess('Approved successfully');
			markAll(false);
		} catch (error) {
			toastError(error?.response?.data?.message || 'Something went wrong');
		}
	};

	const onCloseEodModal = () => {
		setShowEodModal(false);
		markAll(false);
	};

	const onSuccessEodUpdate = async () => {
		await onRefreshList();
		setShowEodModal(false);
		markAll(false);
	};

	const isCostingManger = () => checkUserType('COSTING_MANAGER');
	const isAllSelected = listViewResponse?.length === selectedIds?.length;

	const isValidInput = (prevValueRef, eventOrValue) => {
		const nextValue =
			typeof eventOrValue === 'string' ? eventOrValue : (eventOrValue?.target?.value ?? '');
		if (String(prevValueRef.current ?? '') === String(nextValue ?? '')) return false;
		if (nextValue !== '' && Number.isNaN(parseFloat(nextValue))) {
			toastError('Please enter a valid target price');
			return false;
		}
		return true;
	};
	const isValidTextInput = (prevValueRef, eventOrValue) => {
		const nextValue =
			typeof eventOrValue === 'string' ? eventOrValue : (eventOrValue?.target?.value ?? '');
		return String(prevValueRef.current ?? '') !== String(nextValue ?? '');
	};

	const getStatus = (status) => {
		if (status === 'APPROVED') return 'green';
		return 'secondary';
	};

	const getOfferPrice = (item, marginValue) => {
		const offerStatus = item?.status ?? item?.costingJourneyDetailsResponse?.quoteItemResponse?.status;
		const adminOfferPrice = item?.costingJourneyDetailsResponse?.quoteItemResponse?.adminOfferPrice;
		const currentMargin = item?.costingJourneyDetailsResponse?.quoteItemResponse?.margin ?? marginValue;
		const priceUpdateDTOArray =
			item?.priceUpdateDTOArray ??
			item?.costingJourneyDetailsResponse?.quoteItemResponse?.priceUpdateDTOArray;
		const lastPriceUpdate = priceUpdateDTOArray?.[priceUpdateDTOArray?.length - 1];

		return (
			<div className={`regular-14 ${getStatus(offerStatus)} weight-500`}>
				{adminOfferPrice && (
					<span className="d-block">
						{offerStatus === COSTING_OFFER_STATUS.OFFER_SENT && (
							<img
								src={`${process.env.PUBLIC_URL}/icons/arrow_forward.svg`}
								className="offer-sent"
								alt="offer sent"
							/>
						)}
						{offerStatus === COSTING_OFFER_STATUS.REQUEST_FOR_REVISION ? (
							<span className="regular-14  red weight-500">
								${parseFloat(adminOfferPrice).toFixed(2)}
							</span>
						) : (
							<>${parseFloat(adminOfferPrice).toFixed(2)}</>
						)}
						{currentMargin >= 0 && <span className="pl-1 gray_dark_01">({currentMargin}%)</span>}
					</span>
				)}
				{priceUpdateDTOArray?.length > 0 && (
					<p className="regular-14  gray_dark_02 weight-500">
						<del>${parseFloat(lastPriceUpdate?.previousValue).toFixed(2)}</del>
						{lastPriceUpdate?.margin >= 0 && (
							<span className="pl-1 gray_dark_01">({lastPriceUpdate?.margin}%)</span>
						)}
					</p>
				)}
			</div>
		);
	};

	const onUpdateItem = async ({ id, targetPrice, margin, remarks }) => {
		try {
			const body = {
				id,
				...(targetPrice !== undefined ? { targetPrice } : {}),
				...(margin !== undefined ? { margin } : {}),
				...(remarks !== undefined ? { remarks } : {})
			};
			await quantityWiseUpdatePrice({
				body
			}).unwrap();
			resetAll();
			await onRefreshList();
			toastSuccess(
				targetPrice !== undefined
					? 'Target price updated successfully'
					: margin !== undefined
						? 'Offer price updated successfully'
						: 'Remarks updated successfully'
			);
		} catch (error) {
			toastError(error?.data?.message || 'Something went wrong');
		}
	};

	const renderConstingListViewRow = (item, idx) => {
		const quantityWiseCostingId = getRowTargetId(item);
		const targetValue = getDisplayTargetValue(item);
		const isTargetCellEditing = cellId === getTargetCellId(quantityWiseCostingId);
		const productionPrice =
			item?.productionPrice ?? item?.costingJourneyDetailsResponse?.quoteItemResponse?.productionPrice;
		const marginValue = Object.prototype.hasOwnProperty.call(offerValues, quantityWiseCostingId)
			? offerValues[quantityWiseCostingId]
			: (item?.costingJourneyDetailsResponse?.quoteItemResponse?.margin ?? '');
		const remarkValue = getDisplayRemarkValue(item);

		const rowKey = `${getRowTargetId(item)}_${idx}`;
		return (
			<Fragment key={rowKey}>
				<tr>
					<td className="select-box-column checkbox-column">
						<div className="custom-chekbox d-inline-block align-middle">
							<div className="form-group m-0">
								<input
									type="checkbox"
									id={`costingSheet_${quantityWiseCostingId}`}
									name={`costingSheet_${quantityWiseCostingId}`}
									data-quantitywiseid={quantityWiseCostingId}
									data-productid={item?.productId ?? item?.id}
									value={item?.id}
									onChange={(e) => onChangeCheckbox(e, item)}
									checked={selectedIds?.some(
										(value) => value?.toString() === quantityWiseCostingId?.toString()
									)}
								/>
								<label htmlFor={`costingSheet_${quantityWiseCostingId}`} />
							</div>
						</div>
					</td>
					<td
						rowSpan={item?.costingJourneyDetailsResponse?.quoteItemResponse?.noOfSameVariant || 1}
						className={`image-column cursor-pointer ${item?.costingJourneyDetailsResponse?.quoteItemResponse?.isSameVariant ? 'd-none' : ''}`}
						onClick={() => {
							setIsShowCosting(false);
							setSelectedIndex(idx);
							setSelectedProductId(item?.productId ?? item?.id);
							setSelectedInitialCostingId(
								item?.initialCostingId ??
									item?.costingJourneyDetailsResponse?.quoteItemResponse?.initialCostingId
							);
						}}
					>
						<div className="d-flex align-items-center justify-content-center">
							<img src={item.designDocuments?.[0]?.docUrl} alt="" className="cell-img pr-2" />
						</div>
					</td>
					<td
						className={`style-details-column ${item?.costingJourneyDetailsResponse?.quoteItemResponse?.isSameVariant ? 'd-none' : ''}`}
						rowSpan={item?.costingJourneyDetailsResponse?.quoteItemResponse?.noOfSameVariant || 1}
					>
						<p className="semibold-14 gray_dark_02 mb-2">{item?.systemReferenceNumber}</p>
						<p className="regular-14 gray_dark_02 mb-0">
							{item?.productGroup}-{item?.mainFabricType}
						</p>
						<p className="regular-14 gray_dark_02 mb-0">
							{item?.subCategory
								? `${item?.subCategory.toLowerCase().charAt(0).toUpperCase() + item?.subCategory.toLowerCase().slice(1)}`
								: ''}
						</p>
					</td>
					<td
						className={`body-fabric-column ${item?.costingJourneyDetailsResponse?.quoteItemResponse?.isSameVariant ? 'd-none' : ''}`}
						rowSpan={item?.costingJourneyDetailsResponse?.quoteItemResponse?.noOfSameVariant || 1}
					>
						<p className="regular-14 gray_dark_02 mb-0">
							{item?.mainFabricType
								? item?.mainFabricType.toLowerCase().charAt(0).toUpperCase() +
									item?.mainFabricType.toLowerCase().slice(1)
								: ''}
							{item?.construction ? `, ${item?.construction}` : ''}
							{item?.fabricDetails ? `, ${item?.fabricDetails}` : ''}
						</p>
					</td>

					<td
						className={`print-embo-column ${item?.costingJourneyDetailsResponse?.quoteItemResponse?.isSameVariant ? 'd-none' : ''}`}
						rowSpan={item?.costingJourneyDetailsResponse?.quoteItemResponse?.noOfSameVariant || 1}
					>
						{item?.costingJourneyDetailsResponse?.printTypes?.length > 0 && (
							<p className="regular-14 gray_dark_02 mb-0">
								Print:{' '}
								<span className="gray_dark_01">
									{item?.costingJourneyDetailsResponse?.printTypes?.join(', ')}
								</span>
							</p>
						)}
						{item?.costingJourneyDetailsResponse?.emboTypes?.length > 0 && (
							<p className="regular-14 gray_dark_02 mb-0">
								Embo:{' '}
								<span className="gray_dark_01">
									{item?.costingJourneyDetailsResponse?.emboTypes?.join(', ')}
								</span>
							</p>
						)}
					</td>
					<td
						className={`wash-column ${item?.costingJourneyDetailsResponse?.quoteItemResponse?.isSameVariant ? 'd-none' : ''}`}
						rowSpan={item?.costingJourneyDetailsResponse?.quoteItemResponse?.noOfSameVariant || 1}
					>
						<p className="regular-14 gray_dark_02 mb-0">
							{item?.costingJourneyDetailsResponse?.washTypes?.join(', ')}
						</p>
					</td>
					<td
						className="qty-column cursor-pointer"
						onClick={() => {
							setIsShowCosting(true);
							setSelectedIndex(idx);
							setSelectedProductId(item?.productId ?? item?.id);
							setSelectedInitialCostingId(
								item?.initialCostingId ??
									item?.costingJourneyDetailsResponse?.quoteItemResponse?.initialCostingId
							);
						}}
					>
						<p className="regular-14 gray_dark_02 mb-0">
							{item?.costingJourneyDetailsResponse?.quoteItemResponse?.quantity}
						</p>
					</td>
					<td
						className="buyer-target-price-column cursor-pointer"
						onClick={() => onSetCellId(quantityWiseCostingId, TARGET_FIELD)}
					>
						{isTargetCellEditing ? (
							<input
								type="number"
								autoComplete="off"
								name="target"
								value={targetValue || ''}
								placeholder="$00.00"
								autoFocus
								onFocus={(event) => (prevTarget.current = event.target.value)}
								onBlur={(event) => {
									if (!isValidInput(prevTarget, event)) {
										resetAll();
										return;
									}
									onUpdateItem({
										id: quantityWiseCostingId,
										targetPrice: parseFloat(getDisplayTargetValue(item)) || null
									});
								}}
								onChange={(event) =>
									setTargetValues((prev) => ({
										...prev,
										[quantityWiseCostingId]: event.target.value
									}))
								}
								onKeyDown={(event) => {
									if (event.key === 'Enter') {
										if (!isValidInput(prevTarget, event)) {
											resetAll();
											return;
										}
										onUpdateItem({
											id: quantityWiseCostingId,
											targetPrice: parseFloat(getDisplayTargetValue(item)) || null
										});
									}
								}}
								disabled={isUpdatingPrice}
							/>
						) : (
							<p className="regular-14 gray_dark_02 mb-0">
								{targetValue === '' ||
								targetValue == null ||
								Number.isNaN(parseFloat(targetValue))
									? ' '
									: `$${parseFloat(targetValue).toFixed(2)}`}
							</p>
						)}
					</td>
					<td
						className="production-price-column cursor-pointer"
						onClick={() => {
							setIsShowCosting(true);
							setSelectedIndex(idx);
							setSelectedProductId(item?.productId ?? item?.id);
							setSelectedInitialCostingId(
								item?.initialCostingId ??
									item?.costingJourneyDetailsResponse?.quoteItemResponse?.initialCostingId
							);
						}}
					>
						<span className="regular-14 gray_dark_01">
							{productionPrice ? (
								`$${parseFloat(productionPrice).toFixed(2)}`
							) : (
								<span className="gray_dark_02 add-price">Add price</span>
							)}
							{item?.isQuoted && productionPrice > 0 && !item?.isFactoryCosting && (
								<img
									className="pl-2"
									src={`${process.env.PUBLIC_URL}/icons/shiped.svg`}
									alt="quoted"
								/>
							)}
							{item?.isQuoted && item?.isFactoryCosting && (
								<img
									className="pl-2"
									src={`${process.env.PUBLIC_URL}/icons/factory.svg`}
									alt="factory"
								/>
							)}
						</span>
					</td>
					<td
						onClick={() => onSetCellId(quantityWiseCostingId, OFFER_FIELD)}
						className="offer-price-column"
					>
						{cellId === `${quantityWiseCostingId}_${OFFER_FIELD}` ? (
							<input
								type="number"
								autoComplete="off"
								name="margin"
								value={marginValue}
								placeholder="Margin %"
								autoFocus
								onFocus={(event) => (prevOffer.current = event.target.value)}
								onBlur={(event) => {
									if (!isValidInput(prevOffer, event)) {
										resetAll();
										return;
									}
									onUpdateItem({
										id: quantityWiseCostingId,
										margin: parseFloat(marginValue) || null
									});
								}}
								onChange={(event) =>
									setOfferValues((prev) => ({
										...prev,
										[quantityWiseCostingId]: event.target.value
									}))
								}
								onKeyDown={(event) => {
									if (event.key === 'Enter') {
										onUpdateItem({
											id: quantityWiseCostingId,
											margin: parseFloat(marginValue) || null
										});
									}
								}}
								disabled={isUpdatingPrice}
							/>
						) : (
							getOfferPrice(item, marginValue)
						)}
					</td>
					<td
						className={`remarks-column cursor-pointer text-field-column ${item?.costingJourneyDetailsResponse?.quoteItemResponse?.isSameVariant ? 'd-none' : ''}`}
						rowSpan={item?.costingJourneyDetailsResponse?.quoteItemResponse?.noOfSameVariant || 1}
						onClick={() => onSetCellId(quantityWiseCostingId, REMARK_FIELD)}
					>
						{cellId === `${quantityWiseCostingId}_${REMARK_FIELD}` ? (
							<textarea
								autoComplete="off"
								name="remarks"
								value={remarkValue}
								autoFocus
								onFocus={(event) => (prevRemark.current = event.target.value)}
								onBlur={(event) => {
									if (!isValidTextInput(prevRemark, event)) {
										resetAll();
										return;
									}
									onUpdateItem({
										id: quantityWiseCostingId,
										remarks: remarkValue
									});
								}}
								onChange={(event) =>
									setRemarkValues((prev) => ({
										...prev,
										[quantityWiseCostingId]: event.target.value
									}))
								}
								onKeyDown={(event) => {
									if (event.key === 'Enter') {
										event.preventDefault();
										if (!isValidTextInput(prevRemark, event)) {
											resetAll();
											return;
										}
										onUpdateItem({
											id: quantityWiseCostingId,
											remarks: remarkValue
										});
									}
								}}
								disabled={isUpdatingPrice}
							/>
						) : (
							<span className="regular-14 gray_dark_02">{remarkValue}</span>
						)}
					</td>
					<td
						className={`last-status-column ${item?.costingJourneyDetailsResponse?.quoteItemResponse?.isSameVariant ? 'd-none' : ''}`}
						rowSpan={item?.costingJourneyDetailsResponse?.quoteItemResponse?.noOfSameVariant || 1}
					>
						<span className="status yellow-1 status-with-border d-inline-block mb-1">
							{item?.costingJourneyDetailsResponse?.lastStatusName}
						</span>
						<p className="regular-14  gray_dark_02 mb-0">
							{formatDashboardDate(item?.costingJourneyDetailsResponse?.lastStatusDate)},
							{item?.costingJourneyDetailsResponse?.lastStatusByName}
						</p>
					</td>
					<td
						rowSpan={item?.costingJourneyDetailsResponse?.quoteItemResponse?.noOfSameVariant || 1}
						className={`${item?.costingJourneyDetailsResponse?.quoteItemResponse?.isSameVariant ? 'd-none' : ''}`}
					>
						{item?.costingJourneyDetailsResponse?.actualLeadTimeInDays && (
							<p className="regular-14  gray_dark_02 mb-0">
								{item?.costingJourneyDetailsResponse?.actualLeadTimeInDays} D
							</p>
						)}
					</td>
					<td
						className={`lead-time-column ${item?.costingJourneyDetailsResponse?.quoteItemResponse?.isSameVariant ? 'd-none' : ''}`}
						rowSpan={item?.costingJourneyDetailsResponse?.quoteItemResponse?.noOfSameVariant || 1}
					>
						{renderLeadTimeVariance(
							item?.costingJourneyDetailsResponse?.requiredLeadTimeInDays,
							item?.costingJourneyDetailsResponse?.actualLeadTimeInDays,
							'days'
						)}
					</td>
					<td
						className={`request-column date-column ${item?.costingJourneyDetailsResponse?.quoteItemResponse?.isSameVariant ? 'd-none' : ''}`}
						rowSpan={item?.costingJourneyDetailsResponse?.quoteItemResponse?.noOfSameVariant || 1}
					>
						{item?.costingJourneyDetailsResponse?.requestDate && (
							<p className="regular-14  gray_dark_02 mb-0">
								Requested:{' '}
								<span className="gray_dark_01">
									{formatDashboardDate(item?.costingJourneyDetailsResponse?.requestDate)},{' '}
									{item?.costingJourneyDetailsResponse?.requestedByName}
								</span>
							</p>
						)}
						{item?.costingJourneyDetailsResponse?.requestReceivedDate && (
							<p className="regular-14  gray_dark_02 mb-0">
								Rcv:{' '}
								<span className="gray_dark_01">
									{formatDashboardDate(
										item?.costingJourneyDetailsResponse?.requestReceivedDate
									)}
									, {item?.costingJourneyDetailsResponse?.requestReceivedByName}
								</span>
							</p>
						)}
						{item?.costingJourneyDetailsResponse?.quoteItemResponse?.expectedOrderDate && (
							<p className="regular-14  gray_dark_02 mb-0">
								Expected order:{' '}
								<span className="gray_dark_01">
									{formatDashboardDate(
										item?.costingJourneyDetailsResponse?.quoteItemResponse
											?.expectedOrderDate
									)}
								</span>
							</p>
						)}
					</td>
					<td
						className={`required-complete-conversation-column date-column ${item?.costingJourneyDetailsResponse?.quoteItemResponse?.isSameVariant ? 'd-none' : ''}`}
						rowSpan={item?.costingJourneyDetailsResponse?.quoteItemResponse?.noOfSameVariant || 1}
					>
						{item?.costingJourneyDetailsResponse?.quoteItemResponse?.requiredDate && (
							<p className="regular-14  gray_dark_02 mb-0">
								Required:{' '}
								<span className="gray_dark_01">
									{formatDashboardDate(
										item?.costingJourneyDetailsResponse?.quoteItemResponse?.requiredDate
									)}
								</span>
							</p>
						)}
						{item?.costingJourneyDetailsResponse?.completedDate && (
							<p className="regular-14  gray_dark_02 mb-0">
								Completed:
								<span className="gray_dark_01">
									{formatDashboardDate(item?.costingJourneyDetailsResponse?.completedDate)},{' '}
									{item?.costingJourneyDetailsResponse?.CompletedByName}
								</span>
							</p>
						)}
						{item?.costingJourneyDetailsResponse?.offerSendToBuyerDate && (
							<p className="regular-14  gray_dark_02 mb-0">
								Send to buyer:
								<span className="gray_dark_01">
									{formatDashboardDate(
										item?.costingJourneyDetailsResponse?.offerSendToBuyerDate
									)}
								</span>
							</p>
						)}
						<div className="conversation-btn">
							<button
								type="button"
								className="button text"
								onClick={(e) => {
									e.stopPropagation();
									const productId =
										item?.productId ??
										item?.costingJourneyDetailsResponse?.quoteItemResponse?.productId ??
										item?.id;
									if (!productId) {
										return;
									}
									dispatch(
										setCurrentActivePage({
											id: productId,
											type: 'PRODUCT_CONVERSATION',
											pageType: 'DETAILS'
										})
									);
									setConversationProductId(productId);
									setShowComment(true);
								}}
							>
								{item?.noOfUnreadMessage > 0 ||
								item?.costingJourneyDetailsResponse?.quoteItemResponse?.noOfUnreadMessage >
									0 ? (
									<CommonImage
										className="cursor-pointer"
										src="/icons/comment-not-read.svg"
										width={24}
										height={24}
										alt=""
									/>
								) : (
									<CommonImage
										className="cursor-pointer"
										src="/icons/comments-gray.svg"
										width={24}
										height={24}
										alt=""
									/>
								)}
							</button>
						</div>
					</td>
					<td
						className={`review-column date-column ${item?.costingJourneyDetailsResponse?.quoteItemResponse?.isSameVariant ? 'd-none' : ''}`}
						rowSpan={item?.costingJourneyDetailsResponse?.quoteItemResponse?.noOfSameVariant || 1}
					>
						{item?.costingJourneyDetailsResponse?.approvedByBuyerDate && (
							<p>
								Approved by the buyer:{' '}
								<span className="gray_dark_01">
									{formatDashboardDate(
										item?.costingJourneyDetailsResponse?.approvedByBuyerDate
									)}
								</span>
							</p>
						)}
						{item?.costingJourneyDetailsResponse?.costingClosedWithoutOrderDate && (
							<p>
								Costing closed without order/Sampling:{' '}
								<span className="gray_dark_01">
									{formatDashboardDate(
										item?.costingJourneyDetailsResponse?.costingClosedWithoutOrderDate
									)}
								</span>
							</p>
						)}
					</td>
				</tr>
			</Fragment>
		);
	};
	return (
		<div>
			<div className="single-table list-view-table-container costing-dashboard-container">
				<HorizontalScroll elementReference={elementRef.current} />
				<div className="header-sticky-table task-configuration-costing-details-view-table">
					<table className="table with-border scroll-x-label scroll-y-label" ref={elementRef}>
						<thead>
							<tr>
								<th className="select-box-column checkbox-column">
									<div className="custom-chekbox d-inline-block align-middle">
										<div className="form-group m-0">
											<input
												type="checkbox"
												id="costingSheetAll"
												name="costingSheetAll"
												onChange={(e) => handleSelectAllCostingItem(e)}
												checked={
													rows?.length > 0 &&
													rows?.every((row) =>
														selectedIds?.some(
															(value) =>
																value?.toString() ===
																getRowTargetId(row)?.toString()
														)
													)
												}
											/>
											<label htmlFor="costingSheetAll"></label>
										</div>
									</div>
								</th>
								<th>Image</th>
								<th className="white-space-nowrap">Style details</th>
								<th className="white-space-nowrap">Body fabric</th>
								<th className="white-space-nowrap">Print,Embro</th>
								<th>Wash</th>
								<th>Qty</th>
								<th className="white-space-nowrap">Buyer target price</th>
								<th className="white-space-nowrap">Production price</th>
								<th className="white-space-nowrap">Offer price</th>
								<th>Remarks</th>
								<th className="white-space-nowrap">Last status</th>
								<th className="white-space-nowrap">
									Actual lead time: <br />
									Request - Completed
								</th>
								<th className="white-space-nowrap">
									Required lead time: <br /> Request - Required
								</th>
								<th>Request</th>
								<th className="white-space-nowrap">Required &amp; complete</th>
								<th>Review</th>
							</tr>
						</thead>
						<tbody>
							{displayGroupEntries?.length ? (
								displayGroupEntries.map(([groupName, items]) => (
									<Fragment key={`group_${groupName}`}>
										<tr className="sample-journey-group-header-row">
											<td
												colSpan={17}
												className="group-title-header bg-white border-bottom-0 pt-3 pb-2"
											>
												<div className="group-title-header-content">
													<span className="semibold-14 mr-2 gray_dark_01">
														{selectedGroupBy?.name === 'Request-wise' ? (
															<span
																className="text-underline cursor-pointer"
																onClick={() =>
																	window.open(
																		`/costing/${items?.[0]?.costingJourneyDetailsResponse?.requestId}`,
																		'_blank'
																	)
																}
															>
																{getShortName(groupName, 50)}
															</span>
														) : (
															<span className="gray_dark_01">
																{getShortName(groupName, 50)}
															</span>
														)}

														<span className="regular-14 gray_dark_01">
															({items?.length ?? 0})
														</span>
													</span>
												</div>
											</td>
										</tr>
										{items?.map((item, idx) => renderConstingListViewRow(item, idx))}
									</Fragment>
								))
							) : rows?.length > 0 ? (
								rows?.map((item, idx) => renderConstingListViewRow(item, idx))
							) : (
								<tr>
									<td className="text-center align-middle" colSpan={17}>
										Data not found
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
				{conversationProductId && (
					<div className="col-md-1 one-sixth text-right">
						<SampleComment
							sampleRequestId={listViewResponse?.[0]?.costingJourneyDetailsResponse?.requestId}
							postTypeFilter={'PRODUCT_DEVELOPMENT_COMMENT'}
							topic={'PRODUCT_QUOTE_COMMENT'}
							postTypeId={'quoteRequestId'}
							productId={conversationProductId}
							showComment={showComment}
							setShowComment={() => handleCommentClose()}
							brandInfo={{
								brandId:
									listViewResponse?.[0]?.costingJourneyDetailsResponse?.brandResponse?.id,
								brandName:
									listViewResponse?.[0]?.costingJourneyDetailsResponse?.brandResponse?.name
							}}
						/>
					</div>
				)}
			</div>
			{selectedIds.length > 0 && (
				<div className="design-count-section costing-tab bg-white collection-details-design-count">
					<div className="count-info d-flex justify-content-between align-items-center">
						<div className="design-count">
							<p className="regular-14">{selectedIds?.length}</p>
						</div>
						<button
							className="button text size36"
							onClick={() => {
								if (selectAll) {
									markAll(false);
								} else {
									markAll(true);
								}
							}}
						>
							<span className="ml-1">{isAllSelected ? 'Unselect All ' : 'Select All'}</span>
						</button>

						{!isCostingManger() && (
							<>
								<span className="regular-16 gray_dark_01 d-inline-block mx-2  selection-devider">
									|
								</span>
								{checkIfSameSimilarStatus() === 'DIFFERENT' ? (
									<span className="regular-14 red d-flex align-items-center mt-1 mx-2">
										<img
											src={`${process.env.PUBLIC_URL}/icons/status-overdue.svg`}
											className="mr-2"
											alt="warning"
										/>
										{notSimilarStatus}
									</span>
								) : !checkIfAllProductionPriceAvailable() ? (
									<span className="regular-14 red d-flex align-items-center mt-1 mx-2">
										<img
											src={`${process.env.PUBLIC_URL}/icons/status-overdue.svg`}
											className="mr-2"
											alt="warning"
										/>
										{productionPriceMissing}
									</span>
								) : (
									<div className="count-info d-flex justify-content-between align-items-center">
										<button
											className="button text size36"
											onClick={() =>
												setConfirmationModal({
													isOpen: true,
													type: 'SEND_OFFER'
												})
											}
											disabled={selectedIds.length < 1}
										>
											<span>Send offer</span>
										</button>
										<button
											className="button text size36"
											onClick={() =>
												setConfirmationModal({
													isOpen: true,
													type: 'APPROVE'
												})
											}
											disabled={
												selectedIds.length < 1 ||
												checkIfDisable(COSTING_OFFER_STATUS.OFFER_SENT)
											}
										>
											<span>Approved</span>
										</button>
									</div>
								)}

								<span className="regular-16 gray_dark_01 d-inline-block mx-2  selection-devider">
									|
								</span>

								<button className="button text size36" onClick={() => setShowEodModal(true)}>
									<img
										src={`${process.env.PUBLIC_URL}/icons/calendar-dark.svg`}
										alt="download"
									/>
									<span className="ml-1">EOD</span>
								</button>
							</>
						)}

						<button className="button text size36" onClick={() => setIsShowTaskUpdateModal(true)}>
							<span className="mr-1">Update task</span>
						</button>

						<div className="text-right close-icon cursor-pointer" onClick={() => markAll(false)}>
							<img src={`${process.env.PUBLIC_URL}/icons/close.svg`} alt="close" />
						</div>
					</div>
				</div>
			)}
			{confirmationModal.isOpen && (
				<ConfirmationModal
					title={confirmationModal.type === 'SEND_OFFER' ? 'Send Offer' : 'Approve'}
					description={
						confirmationModal.type === 'SEND_OFFER'
							? 'you want to send the offer for the selected items?'
							: 'you want to approve the selected items?'
					}
					closeModal={() => setConfirmationModal({ isOpen: false, type: null })}
					callback={() => {
						setConfirmationModal({ isOpen: false, type: null });
						confirmationModal.type === 'SEND_OFFER' ? onSendOffer() : onApproveOrRejct();
					}}
				/>
			)}
			{showEodModal && (
				<CostingEodModal
					selectedDesignIds={selectedProductIds}
					selectedItem={selectedItem}
					onCloseModal={onCloseEodModal}
					onSuccessEodUpdate={onSuccessEodUpdate}
				/>
			)}

			{isShowTaskUpdateModal && (
				<AddTaskModal
					onClose={() => {
						setIsShowTaskUpdateModal(false);
						markAll(false);
					}}
					productIdList={selectedTaskProductIds}
					excludeTasks={[
						'COSTING_REJECTED',
						'BUYER_COSTING_REQUESTED_FROM_BUYER_PLATFORM',
						'CANCEL_BUYER_PLATFORM_COSTING_REQUEST',
						'BUYER_COSTING_REQUESTED_FROM_AM_BDM'
					]}
					journeyType={'COSTING'}
					quoteRequestId={listViewResponse?.[0]?.costingJourneyDetailsResponse?.requestId}
					excludeJourneyTypes={''}
				/>
			)}
		</div>
	);
};
export default ConstingListView;
